import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  CalendarDays,
  Clock3,
  LayoutList,
  LockKeyhole,
  MessageCircleMore,
  Orbit,
  ShieldBan,
  Loader,
  Send,
  UnlockKeyhole,
  UserRoundCheck,
  UserRoundPlus,
  Users,
  X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import PostItem from "../components/PostItem";
import { createConversation, sendMessage } from "../api/messages";
import { fetchPostsPage } from "../api/posts";
import { fetchSpaces, type Space } from "../api/spaces";
import {
  getProfile,
  getPublicProfile,
  respondFriendRequest,
  toggleBlock,
  toggleFriend,
  type User,
} from "../api/profile";
import type { Post } from "../types";
import { hasIdentity, requireIdentity } from "../utils/identityGate";

type TabId = "posts" | "spaces";
type ReturnState = { from?: string; label?: string; postId?: string; restoreFeed?: boolean };
type FriendshipStatus = "none" | "outgoing" | "incoming" | "friends";
type MessagePolicy = {
  canMessage: boolean;
  requestRequired: boolean;
  reason: "self" | "blocked" | "presence" | "no_one" | "friends_only" | "direct" | "message_request";
};

const PROFILE_POST_BATCH = 6;
const PROFILE_SPACE_BATCH = 6;

function userId(user: any): string {
  return String(user?._id || user?.id || "");
}

function initials(user?: any) {
  const name = user?.displayName || user?.username || "?";
  return String(name).slice(0, 1).toUpperCase();
}

function cleanUsername(value?: string | null) {
  return String(value || "").trim().replace(/^@+/, "");
}

function formatHandle(value?: string | null) {
  const clean = cleanUsername(value);
  return clean ? `@${clean}` : "";
}

function formatCompactMonthYear(value?: string) {
  if (!value) return "Now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Now";
  const month = date.toLocaleDateString(undefined, { month: "short" });
  return `${month} ${String(date.getFullYear()).slice(-2)}`;
}

function compactNumber(value: number) {
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function safeNumber(value: unknown, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function postKey(post: any) {
  return String(post?.id || post?._id || `${post?.createdAt || ""}-${String(post?.content || "").slice(0, 24)}`);
}

function spaceId(space: any) {
  return String(space?.id || space?._id || "");
}

function spaceTitle(space: any) {
  return String(space?.title || space?.name || space?.displayName || "Untitled space");
}

function spaceDescription(space: any) {
  return String(space?.desc || space?.description || space?.summary || "A Blackspace community.");
}

function spaceMemberCount(space: any) {
  return safeNumber(space?.memberCount ?? space?.membersCount ?? space?.members ?? space?.archivedMemberCount, 0);
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-800 bg-black/35 p-8 text-center">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-950 text-neutral-500">
        {icon}
      </div>
      <h2 className="text-base font-black text-white">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-500">{body}</p>
    </div>
  );
}

function Avatar({ user, size = "large" }: { user: any; size?: "hero" | "large" | "small" }) {
  const classes =
    size === "hero"
      ? "h-28 w-28 text-5xl sm:h-32 sm:w-32 sm:text-6xl"
      : size === "large"
      ? "h-24 w-24 text-3xl"
      : "h-11 w-11 text-sm";

  if (user?.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        className={`${classes} bs-profile-avatar shrink-0 border border-cyan-400/25 object-cover shadow-[0_0_60px_rgba(34,211,238,0.14)]`}
      />
    );
  }

  return (
    <div className={`${classes} bs-profile-avatar flex shrink-0 items-center justify-center border border-cyan-400/25 bg-cyan-950/20 font-black text-cyan-200 shadow-[0_0_60px_rgba(34,211,238,0.14)]`}>
      {initials(user)}
    </div>
  );
}

function SpaceCard({ space, onOpen }: { space: Space; onOpen: (id: string) => void }) {
  const id = spaceId(space);
  const title = spaceTitle(space);
  const desc = spaceDescription(space);
  const members = spaceMemberCount(space);
  return (
    <button
      onClick={() => id && onOpen(id)}
      disabled={!id}
      className="group w-full rounded-lg border border-neutral-800 bg-neutral-950/90 p-4 text-left transition-colors hover:border-cyan-700/80 hover:bg-neutral-900/80"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-violet-600/30 bg-violet-950/20 text-sm font-black text-violet-200">
          {title.slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="truncate text-sm font-black text-white group-hover:text-cyan-100">{title}</h3>
            {space.isJoined && <span className="shrink-0 rounded-md border border-cyan-500/20 bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-200">Joined</span>}
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-neutral-400">{desc}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-neutral-500">
            <span className="inline-flex items-center gap-1"><Users size={13} />{compactNumber(members)} members</span>
            <span>{space.category || "General"}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function UserProfile() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = (location.state as ReturnState | null) || {};
  const isAnonymous = !hasIdentity();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [user, setUser] = useState<any>(null);
  const [, setIsFriend] = useState(false);
  const [currentIsPresence, setCurrentIsPresence] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus>("none");
  const [incomingRequestId, setIncomingRequestId] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [accountVisibility, setAccountVisibility] = useState<"public" | "private">("public");
  const [canViewFullProfile, setCanViewFullProfile] = useState(true);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsNextCursor, setPostsNextCursor] = useState<string | null>(null);
  const [postsTotal, setPostsTotal] = useState<number | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesPage, setSpacesPage] = useState(1);
  const [spacesHasMore, setSpacesHasMore] = useState(false);
  const [spacesTotal, setSpacesTotal] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("posts");
  useEffect(() => {
    setActiveTab(new URLSearchParams(location.search).get("tab") === "spaces" ? "spaces" : "posts");
  }, [username, location.search, location.key]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [profileMessageOpen, setProfileMessageOpen] = useState(false);
  const [profileMessageDraft, setProfileMessageDraft] = useState("");
  const [profileConversationId, setProfileConversationId] = useState<string | null>(null);
  const [profileMessageStatus, setProfileMessageStatus] = useState<string | null>(null);
  const postsLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const [messagePolicy, setMessagePolicy] = useState<MessagePolicy>({
    canMessage: false,
    requestRequired: false,
    reason: "self",
  });

  const isSelf = Boolean(
    currentUser?.username &&
    user?.username &&
    cleanUsername(currentUser.username).toLowerCase() === cleanUsername(user.username).toLowerCase()
  );
  const profileUsername = cleanUsername(user?.username);
  const profileHandle = formatHandle(profileUsername);

  const returnLabel = returnState.label || "Feed";
  const returnPath = returnState.from || "/home";
  const shouldRestoreFeed = returnState.restoreFeed ?? returnPath.startsWith("/home");

  const goBack = useCallback(() => {
    navigate(returnPath, {
      replace: false,
      state: {
        restoreFeed: shouldRestoreFeed,
        postId: returnState.postId,
      },
    });
  }, [navigate, returnPath, returnState.postId, shouldRestoreFeed]);

  const closeInlineMessage = useCallback(() => {
    setProfileMessageOpen(false);
    setProfileMessageStatus(null);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (blockConfirmOpen) {
        setBlockConfirmOpen(false);
        return;
      }
      if (profileMessageOpen) {
        closeInlineMessage();
        return;
      }
      goBack();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [blockConfirmOpen, closeInlineMessage, goBack, profileMessageOpen]);

  const loadProfile = useCallback(async (silent = false) => {
    if (!username) return;
    if (!silent) setLoading(true);
    setError(null);

    try {
      const [profileRes, publicRes] = await Promise.all([
        getProfile().catch(() => null),
        getPublicProfile(cleanUsername(username)),
      ]);

      const ownUser = profileRes?.success ? profileRes.user : null;
      setCurrentUser(ownUser);

      if (!publicRes.success) {
        setError(publicRes.message || "Failed to load profile");
        return;
      }

      const relationship = publicRes.relationship || {};
      const ownIsPresence = Boolean((profileRes as any)?.realm?.isPresence || relationship.currentIsPresence);
      const targetUsername = cleanUsername(publicRes.user.username);
      const targetLower = String(targetUsername || "").toLowerCase();
      const ownProfile = cleanUsername(ownUser?.username).toLowerCase() === targetLower;
      const visibility = publicRes.user.accountVisibility || relationship.accountVisibility || "public";
      const fullProfile = ownProfile || Boolean(relationship.canViewFullProfile ?? visibility !== "private");
      const incoming = relationship.incomingRequest || null;
      const outgoing = relationship.outgoingRequest || null;
      const nextMessagePolicy: MessagePolicy = relationship.messagePolicy || {
        canMessage: Boolean(relationship.canMessage),
        requestRequired: !relationship.isFriend,
        reason: relationship.isFriend ? "direct" : "message_request",
      };

      setUser(publicRes.user);
      setCurrentIsPresence(ownIsPresence);
      setIsFriend(Boolean(relationship.isFriend));
      setFriendshipStatus(
        relationship.isFriend ? "friends" : incoming ? "incoming" : outgoing ? "outgoing" : "none"
      );
      setIncomingRequestId(incoming?.id || null);
      setIsBlocked(Boolean(relationship.isBlocked));
      setAccountVisibility(visibility);
      setCanViewFullProfile(fullProfile);
      setMessagePolicy(nextMessagePolicy);
      if (!nextMessagePolicy.canMessage) {
        setProfileMessageOpen(false);
        setProfileMessageDraft("");
        setProfileConversationId(null);
        setProfileMessageStatus(null);
      }

      const [postPage, spacesPage] = fullProfile
        ? await Promise.all([
            fetchPostsPage(undefined, { author: targetUsername, limit: PROFILE_POST_BATCH }),
            fetchSpaces({ limit: PROFILE_SPACE_BATCH, profile: targetUsername, page: 1 }),
          ])
        : [{ items: [] }, { spaces: [] }];
      const postResult = postPage as any;
      const spacesResult = spacesPage as any;

      setPosts(listOf<Post>(postResult.items));
      setPostsNextCursor(postResult.nextCursor || null);
      setPostsTotal(typeof postResult.total === "number" ? postResult.total : null);
      setSpaces(listOf<Space>(spacesResult.spaces));
      setSpacesPage(spacesResult.page || 1);
      setSpacesHasMore(Boolean(spacesResult.hasMore));
      setSpacesTotal(typeof spacesResult.total === "number" ? spacesResult.total : null);
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 403) {
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      setError(err?.message || "Something went wrong");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate, username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const refresh = () => loadProfile(true);
    const handleVisibility = () => {
      if (!document.hidden) refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadProfile]);

  async function handleFriendToggle() {
    if (isAnonymous) return void requireIdentity("follow this identity");
    const targetId = userId(user);
    if (!targetId || actionLoading || isSelf || currentIsPresence) return;
    setActionLoading("friend");
    try {
      const res = await toggleFriend(targetId);
      if (res.success) {
        const nextIsFriend = Boolean(res.isFriend);
        const nextCanViewFullProfile = nextIsFriend || accountVisibility !== "private";
        setIsFriend(res.isFriend);
        setFriendshipStatus(
          res.friendshipStatus === "friends"
            ? "friends"
            : res.friendshipStatus === "incoming"
              ? "incoming"
              : res.friendshipStatus === "outgoing"
                ? "outgoing"
                : "none"
        );
        setIncomingRequestId(res.friendshipStatus === "incoming" ? res.request?.id || incomingRequestId : null);
        setCanViewFullProfile(nextCanViewFullProfile);
        if (!nextCanViewFullProfile) {
          setPosts([]);
          setPostsNextCursor(null);
          setPostsTotal(null);
          setSpaces([]);
          setSpacesPage(1);
          setSpacesHasMore(false);
          setSpacesTotal(null);
          setProfileMessageOpen(false);
          setProfileMessageDraft("");
          setProfileConversationId(null);
          setProfileMessageStatus(null);
          setActiveTab("posts");
        }
        await loadProfile(true);
      }
    } finally {
      setActionLoading(null);
    }
  }

  async function handleFriendResponse(action: "accept" | "decline") {
    if (isAnonymous) return void requireIdentity("respond to follow requests");
    if (!incomingRequestId || actionLoading || isSelf || currentIsPresence) return;
    setActionLoading(`friend-${action}`);
    try {
      const res = await respondFriendRequest(incomingRequestId, action);
      if (res.success) {
        setIsFriend(res.isFriend);
        setFriendshipStatus(res.friendshipStatus === "friends" ? "friends" : "none");
        setIncomingRequestId(null);
        if (res.isFriend) {
          setCanViewFullProfile(true);
        } else if (accountVisibility === "private") {
          setCanViewFullProfile(false);
          setPosts([]);
          setPostsNextCursor(null);
          setPostsTotal(null);
          setSpaces([]);
          setSpacesPage(1);
          setSpacesHasMore(false);
          setSpacesTotal(null);
          setProfileMessageOpen(false);
          setProfileMessageDraft("");
          setProfileConversationId(null);
          setProfileMessageStatus(null);
          setActiveTab("posts");
        }
        await loadProfile(true);
      }
    } finally {
      setActionLoading(null);
    }
  }

  async function handleBlockToggle() {
    if (isAnonymous) return void requireIdentity("manage blocked identities");
    const targetId = userId(user);
    if (!targetId || actionLoading || isSelf) return;

    setActionLoading("block");
    try {
      const res = await toggleBlock(targetId);
      if (res.success) {
        setIsBlocked(res.isBlocked);
        if (res.action === "blocked") {
          setIsFriend(false);
          setFriendshipStatus("none");
          setIncomingRequestId(null);
        }
        setBlockConfirmOpen(false);
      }
    } finally {
      setActionLoading(null);
    }
  }

  async function handleMessage() {
    if (isAnonymous) return void requireIdentity("send a message");
    const targetId = userId(user);
    if (!targetId || actionLoading || isSelf || isBlocked) return;
    setProfileMessageOpen(true);
    setProfileMessageStatus(null);
    if (profileConversationId) return;
    setActionLoading("message");
    try {
      const conversation = await createConversation(targetId);
      setProfileConversationId(conversation.id);
      if (conversation.requestStatus === "draft") {
        setProfileMessageStatus("Write the first message to send a request.");
      } else if (conversation.requestStatus === "pending") {
        setProfileMessageStatus("Message request pending.");
      }
    } catch (err: any) {
      setProfileMessageStatus(err?.message || "Could not open messages.");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleInlineMessageSend() {
    const text = profileMessageDraft.trim();
    const targetId = userId(user);
    if (!text || !targetId || actionLoading || isSelf || isBlocked) return;
    setActionLoading("send-message");
    setProfileMessageStatus(null);
    try {
      const conversation = profileConversationId
        ? { id: profileConversationId }
        : await createConversation(targetId);
      setProfileConversationId(conversation.id);
      const sent = await sendMessage(conversation.id, text);
      setProfileMessageDraft("");
      setProfileMessageStatus(sent.requestStatus === "pending" || (conversation as any).requestStatus === "pending" || messagePolicy.requestRequired
        ? "Message request sent."
        : "Message sent.");
    } catch (err: any) {
      setProfileMessageStatus(err?.message || "Could not send message.");
    } finally {
      setActionLoading(null);
    }
  }

  const loadMorePosts = useCallback(async () => {
    if (!postsNextCursor || !profileUsername || actionLoading) return;
    setActionLoading("more-posts");
    try {
      const page = await fetchPostsPage(postsNextCursor, { author: profileUsername, limit: PROFILE_POST_BATCH });
      setPosts((current) => [...current, ...listOf<Post>((page as any).items)]);
      setPostsNextCursor(page.nextCursor || null);
      if (typeof page.total === "number") setPostsTotal(page.total);
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, postsNextCursor, profileUsername]);

  async function loadMoreSpaces() {
    if (!spacesHasMore || !profileUsername || actionLoading) return;
    setActionLoading("more-spaces");
    try {
      const next = await fetchSpaces({ limit: PROFILE_SPACE_BATCH, profile: profileUsername, page: spacesPage + 1 });
      setSpaces((current) => [...current, ...listOf<Space>((next as any).spaces)]);
      setSpacesPage(next.page || spacesPage + 1);
      setSpacesHasMore(Boolean(next.hasMore));
      if (typeof next.total === "number") setSpacesTotal(next.total);
    } finally {
      setActionLoading(null);
    }
  }

  const joinedCompactLabel = formatCompactMonthYear(user?.createdAt);
  const profileLocked = !isSelf && !canViewFullProfile;
  const canMessageProfile = !isSelf && !isBlocked && !profileLocked && Boolean(messagePolicy.canMessage);
  const friendsCount = safeNumber(user?.friendsCount ?? (Array.isArray(user?.friends) ? user.friends.length : 0), 0);
  const followersCount = safeNumber(user?.followersCount ?? user?.followerCount ?? (Array.isArray(user?.followers) ? user.followers.length : friendsCount), friendsCount);
  const followingCount = safeNumber(user?.followingCount ?? (Array.isArray(user?.following) ? user.following.length : 0), 0);
  const visiblePostsCount = safeNumber(postsTotal, posts.length);
  const visibleSpacesCount = safeNumber(spacesTotal, spaces.length);
  const targetIsPresence = Boolean(
    user?.isPresence ||
    user?.realm?.isPresence ||
    user?.realmRole === "presence" ||
    user?.role === "presence"
  );
  const profileStats = [
    { label: "Posts", value: visiblePostsCount },
    { label: "Spaces", value: visibleSpacesCount },
    ...(!targetIsPresence ? [
      { label: "Followers", value: followersCount },
      { label: "Following", value: followingCount },
    ] : []),
  ];
  const followActionTitle =
    friendshipStatus === "friends"
      ? "Following"
      : friendshipStatus === "outgoing"
        ? "Requested"
        : "Follow request";

  useEffect(() => {
    if (profileLocked || activeTab !== "posts" || !postsNextCursor) return;
    if (typeof IntersectionObserver === "undefined") return;
    const node = postsLoadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMorePosts();
        }
      },
      { rootMargin: "420px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab, loadMorePosts, postsNextCursor, profileLocked]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <main className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-4">
          <div className="w-full max-w-xl space-y-4">
            <div className="h-32 animate-pulse rounded-lg border border-neutral-800 bg-neutral-950" />
            <div className="grid gap-3 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-24 animate-pulse rounded-lg border border-neutral-800 bg-neutral-950" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <main className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-5 px-4 text-center">
          <h1 className="text-2xl font-semibold text-red-300">{error || "User not found"}</h1>
          <button onClick={goBack} className="inline-flex items-center gap-2 rounded-md bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600">
            <ArrowLeft size={16} />
            Back to {returnLabel}
          </button>
        </main>
      </div>
    );
  }

  const tabs: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
    { id: "posts", label: "Posts", icon: <LayoutList size={15} /> },
    { id: "spaces", label: "Spaces", icon: <Orbit size={15} /> },
  ];
  const displayName = String(user.displayName || "").trim();
  const primaryName = displayName || profileUsername || user.username;
  const showSecondaryMeta = Boolean(displayName);

  return (
    <div className="bs-mobile-profile flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <main className="mx-auto w-full max-w-6xl flex-1 space-y-3 px-4 py-3 pb-32 sm:px-8 sm:py-4">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={16} />
          Back to {returnLabel}
        </button>

        <section className="bs-profile-shell overflow-hidden rounded-[1.35rem] bg-neutral-950 shadow-[0_0_90px_rgba(8,145,178,0.08)]">
          <div className="bs-profile-cover relative h-44 overflow-hidden sm:h-56">
            <div className="bs-profile-cover-stars absolute inset-0" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-black/92" />
          </div>

          <div className="bs-profile-identity-panel relative px-4 pb-5 sm:px-6 sm:pb-6">
            <div className="bs-profile-avatar-slot relative -mt-14 w-max sm:-mt-16">
              <Avatar user={user} size="hero" />
            </div>

            <div className="bs-profile-identity-row mt-2 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
              <div className="min-w-0">
                <div className="bs-profile-name-line flex min-w-0 items-center gap-3">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                    <h1 className="bs-message-page-title flex min-w-0 items-center gap-2">
                      <span className="truncate">{primaryName}</span>
                    </h1>
                    {!showSecondaryMeta && (
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-neutral-400"><CalendarDays size={14} />{joinedCompactLabel}</span>
                    )}
                  </div>
                  {isSelf && (
                    <button
                      type="button"
                      onClick={() => navigate("/settings")}
                      className="bs-profile-inline-edit shrink-0 rounded-lg px-3 py-1.5 text-xs font-black text-cyan-100 transition hover:bg-cyan-300/10 hover:text-cyan-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/35"
                    >
                      Edit
                    </button>
                  )}
                  {!isSelf && !isAnonymous && (
                    <div className="bs-profile-actions bs-profile-social-actions flex shrink-0 items-center gap-2">
                      {!isBlocked && !currentIsPresence && friendshipStatus === "incoming" ? (
                        <>
                          <button
                            onClick={() => handleFriendResponse("accept")}
                            disabled={Boolean(actionLoading) || isBlocked}
                            title="Accept follow request"
                            aria-label="Accept follow request"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-emerald-200 transition hover:-translate-y-0.5 hover:text-emerald-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-300/35 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actionLoading === "friend-accept" ? <Loader size={17} className="animate-spin" /> : <UserRoundCheck size={19} strokeWidth={2.2} />}
                          </button>
                          <button
                            onClick={() => handleFriendResponse("decline")}
                            disabled={Boolean(actionLoading)}
                            title="Decline follow request"
                            aria-label="Decline follow request"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-rose-200 transition hover:-translate-y-0.5 hover:text-rose-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rose-300/35 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actionLoading === "friend-decline" ? <Loader size={17} className="animate-spin" /> : <X size={18} strokeWidth={2.35} />}
                          </button>
                        </>
                      ) : !isBlocked && !currentIsPresence ? (
                        <button
                          onClick={handleFriendToggle}
                          disabled={Boolean(actionLoading)}
                          title={followActionTitle}
                          aria-label={followActionTitle}
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-60 ${
                            friendshipStatus === "friends"
                              ? "text-emerald-200 hover:-translate-y-0.5 hover:text-emerald-50 focus-visible:ring-emerald-300/35"
                              : friendshipStatus === "outgoing"
                                ? "text-violet-200 hover:-translate-y-0.5 hover:text-violet-50 focus-visible:ring-violet-300/35"
                                : "text-cyan-200 hover:-translate-y-0.5 hover:text-cyan-50 focus-visible:ring-cyan-300/35"
                          }`}
                        >
                          {actionLoading === "friend" ? (
                            <Loader size={17} className="animate-spin" />
                          ) : friendshipStatus === "friends" ? (
                            <UserRoundCheck size={19} strokeWidth={2.2} />
                          ) : friendshipStatus === "outgoing" ? (
                            <Clock3 size={18} strokeWidth={2.35} />
                          ) : (
                            <UserRoundPlus size={19} strokeWidth={2.2} />
                          )}
                        </button>
                      ) : null}
                      {canMessageProfile && (
                        <button
                          onClick={handleMessage}
                          disabled={Boolean(actionLoading) || isBlocked}
                          title="Message"
                          aria-label="Message"
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-300/35 disabled:cursor-not-allowed disabled:opacity-60 ${
                            profileMessageOpen
                              ? "text-cyan-50"
                              : "text-sky-200 hover:-translate-y-0.5 hover:text-sky-50"
                          }`}
                        >
                          {actionLoading === "message" ? <Loader size={17} className="animate-spin" /> : <MessageCircleMore size={19} strokeWidth={2.15} />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setBlockConfirmOpen(true)}
                        title={isBlocked ? "Unblock" : "Block"}
                        aria-label={isBlocked ? "Unblock" : "Block"}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-rose-200 transition hover:-translate-y-0.5 hover:text-rose-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rose-300/35"
                      >
                        {isBlocked ? <UnlockKeyhole size={19} strokeWidth={2.2} /> : <ShieldBan size={19} strokeWidth={2.2} />}
                      </button>
                    </div>
                  )}
                </div>
                {showSecondaryMeta && (
                  <div className="bs-profile-meta-row mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-400">
                    <span className="bs-profile-handle inline-flex items-center gap-1.5">{profileHandle}</span>
                    <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} />{joinedCompactLabel}</span>
                  </div>
                )}
                <p className="bs-profile-bio mt-1.5 max-w-3xl text-[0.92rem] leading-6 text-neutral-300">
                  {user.bio || (isSelf ? "Add a bio in Settings to make this identity feel alive." : "No public bio yet.")}
                </p>
              </div>
            </div>

            {!profileLocked && (
              <div className={`bs-profile-statbar mt-5 grid ${targetIsPresence ? "grid-cols-2" : "grid-cols-4"} overflow-hidden rounded-2xl border border-white/[0.06] bg-black/35`}>
                {profileStats.map((item) => (
                  <div key={item.label} className="min-w-0 px-3 py-3 text-center sm:px-5">
                    <p className="text-base font-black text-cyan-200 sm:text-lg">{compactNumber(item.value)}</p>
                    <p className="mt-1 text-[10px] font-bold tracking-[0.06em] text-neutral-500">{item.label}</p>
                  </div>
                ))}
              </div>
            )}

            {profileLocked && (
              <div className="bs-profile-private-card mt-5 rounded-2xl border border-violet-500/20 p-4 backdrop-blur">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-300/25 bg-violet-400/10 text-violet-100">
                    <LockKeyhole size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="bs-profile-private-kicker text-xs font-black uppercase tracking-[0.18em] text-violet-200">
                      {friendshipStatus === "outgoing" ? "Follow request sent" : friendshipStatus === "incoming" ? "Follow request received" : "Private profile"}
                    </p>
                    <h2 className="mt-1 text-base font-black text-white">
                      {friendshipStatus === "outgoing"
                        ? "Full profile unlocks after approval."
                        : friendshipStatus === "incoming"
                          ? "Accept the follow request to open the profile."
                          : "Approved followers can see the full profile."}
                    </h2>
                    <p className="mt-2 text-xs leading-5 text-neutral-400">
                      {friendshipStatus === "outgoing"
                        ? "Posts and spaces unlock once they approve you."
                        : friendshipStatus === "incoming"
                          ? "They are waiting for your response."
                          : currentIsPresence
                            ? "This identity does not use follow requests."
                            : "Send a follow request to unlock posts and spaces."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {!profileLocked && (
          <div className="grid gap-5">
            <div className="min-w-0 space-y-4">
              <nav className="bs-profile-tabs bs-profile-tabs-clean sticky top-3 z-20 flex gap-2 rounded-2xl border border-white/[0.06] bg-black/65 p-1.5 shadow-2xl shadow-black/25 backdrop-blur-xl">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative inline-flex h-10 items-center gap-2 rounded-xl px-4 text-xs font-black transition-colors sm:text-sm ${
                      activeTab === tab.id
                        ? "text-cyan-50"
                        : "text-neutral-500 hover:text-neutral-200"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    {activeTab === tab.id && <span className="absolute inset-x-3 -bottom-1 h-0.5 rounded-full bg-cyan-300 shadow-[0_0_16px_rgba(34,211,238,0.75)]" />}
                  </button>
                ))}
              </nav>

              {activeTab === "posts" && (
                <section className="space-y-3">
                  {posts.map((post) => (
                    <article key={postKey(post)} className="bs-profile-post rounded-2xl border border-white/[0.06] bg-neutral-950/45 p-3 transition-colors hover:border-cyan-300/20">
                      <PostItem post={post} currentUsername={currentUser?.username || ""} onSelfClick={() => {}} />
                    </article>
                  ))}
                  {posts.length === 0 && (
                    <EmptyState icon={<LayoutList size={19} />} title="No public posts yet" body="When this profile posts publicly, the feed will begin here." />
                  )}
                  <div ref={postsLoadMoreRef} className="flex min-h-12 items-center justify-center">
                    {postsNextCursor && (
                      <button
                        type="button"
                        onClick={loadMorePosts}
                        disabled={actionLoading === "more-posts"}
                        className="mx-auto flex min-h-10 items-center justify-center rounded-xl border border-cyan-300/15 bg-cyan-300/10 px-4 text-xs font-black text-cyan-50 transition hover:bg-cyan-300/15 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionLoading === "more-posts" ? <Loader size={15} className="animate-spin" /> : "Show more"}
                      </button>
                    )}
                  </div>
                </section>
              )}

              {activeTab === "spaces" && (
                <section className="grid gap-3 sm:grid-cols-2">
                  {spaces.map((space) => (
                    <SpaceCard key={spaceId(space) || spaceTitle(space)} space={space} onOpen={(id) => navigate(`/spaces/${id}`)} />
                  ))}
                  {spaces.length === 0 && (
                    <div className="sm:col-span-2">
                      <EmptyState icon={<Orbit size={19} />} title="No spaces attached" body="Joined and created spaces will appear here when they are visible to you." />
                    </div>
                  )}
                  {spacesHasMore && (
                    <div className="sm:col-span-2">
                      <button
                        type="button"
                        onClick={loadMoreSpaces}
                        disabled={actionLoading === "more-spaces"}
                        className="mx-auto flex min-h-11 items-center justify-center rounded-xl border border-cyan-300/15 bg-cyan-300/10 px-5 text-sm font-black text-cyan-50 transition hover:bg-cyan-300/15 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionLoading === "more-spaces" ? <Loader size={16} className="animate-spin" /> : "Show more spaces"}
                      </button>
                    </div>
                  )}
                </section>
              )}

            </div>
          </div>
        )}
      </main>

      {canMessageProfile && profileMessageOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeInlineMessage();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label={`Message ${profileHandle}`}
            className="w-full max-w-lg overflow-hidden rounded-[1.45rem] border border-cyan-300/15 bg-[#05070b]/95 shadow-[0_0_50px_rgba(0,0,0,0.75)]"
          >
            <div className="relative overflow-hidden border-b border-white/[0.06] px-4 py-4">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(34,211,238,0.16),transparent_34%),linear-gradient(120deg,transparent,rgba(34,211,238,0.06),transparent)]" />
              <div className="relative flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar user={user} size="small" />
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-black text-white">Message {profileHandle}</h2>
                    <p className="mt-1 text-xs leading-5 text-neutral-500">
                      {messagePolicy.requestRequired ? "This may start as a message request." : "Send directly to this profile."}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeInlineMessage}
                  aria-label="Close message composer"
                  className="grid h-9 w-9 shrink-0 place-items-center text-neutral-500 transition hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-3 px-4 py-4">
              <textarea
                value={profileMessageDraft}
                onChange={(event) => setProfileMessageDraft(event.target.value.slice(0, 1000))}
                onKeyDown={(event) => {
                  if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                    event.preventDefault();
                    handleInlineMessageSend();
                  }
                }}
                placeholder={`Write to ${user.displayName || user.username}...`}
                rows={5}
                className="w-full resize-none rounded-[1.15rem] border border-white/[0.06] bg-black/55 px-4 py-3 text-sm leading-6 text-neutral-100 outline-none transition placeholder:text-neutral-600 focus:border-cyan-300/45 focus:shadow-[0_0_24px_rgba(34,211,238,0.12)]"
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-auto text-xs font-semibold text-neutral-500">{profileMessageDraft.length}/1000</span>
                {profileMessageStatus && (
                  <span className={`text-xs font-semibold ${profileMessageStatus.includes("sent") ? "text-emerald-300" : "text-red-300"}`}>
                    {profileMessageStatus}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-white/[0.06] px-4 py-4 sm:flex-row">
              <button
                type="button"
                onClick={() => profileConversationId && navigate("/message", {
                  state: {
                    conversationId: profileConversationId,
                    from: `/user/${profileUsername}`,
                    label: user.displayName || profileUsername || user.username,
                  },
                })}
                disabled={!profileConversationId}
                className="rounded-xl border border-neutral-800 bg-black px-4 py-3 text-sm font-black text-neutral-300 transition hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Open chat
              </button>
              <button
                type="button"
                onClick={handleInlineMessageSend}
                disabled={!profileMessageDraft.trim() || actionLoading === "send-message"}
                className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-300/15 px-4 py-3 text-sm font-black text-cyan-50 shadow-[0_0_20px_rgba(34,211,238,0.14)] transition hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionLoading === "send-message" ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                Send
              </button>
            </div>
          </section>
        </div>
      )}

      {blockConfirmOpen && (
        <div
          className="fixed inset-0 z-[130] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && actionLoading !== "block") setBlockConfirmOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label={isBlocked ? "Unblock profile" : "Block profile"}
            className="w-full max-w-md overflow-hidden rounded-[1.45rem] border border-red-300/15 bg-[#05070b]/95 shadow-[0_0_50px_rgba(0,0,0,0.75)]"
          >
            <div className="relative overflow-hidden border-b border-white/[0.06] px-4 py-4">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(239,68,68,0.16),transparent_34%),linear-gradient(120deg,transparent,rgba(239,68,68,0.06),transparent)]" />
              <div className="relative flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.9rem] bg-red-400/10 text-red-100 shadow-[0_0_18px_rgba(239,68,68,0.12)]">
                  <Ban size={18} />
                </span>
                <div className="min-w-0">
                  <h2 className="text-lg font-black text-white">{isBlocked ? "Unblock profile?" : "Block profile?"}</h2>
                  <p className="mt-1 text-xs leading-5 text-neutral-500">
                    {isBlocked ? `${profileHandle} will be able to interact again.` : `This limits contact with ${profileHandle} and removes the connection.`}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 px-4 py-4 sm:flex-row">
              <button
                type="button"
                onClick={() => setBlockConfirmOpen(false)}
                disabled={actionLoading === "block"}
                className="rounded-xl border border-neutral-800 bg-black px-4 py-3 text-sm font-black text-neutral-300 transition hover:bg-white/[0.04] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBlockToggle}
                disabled={actionLoading === "block"}
                className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-red-500/15 px-4 py-3 text-sm font-black text-red-50 shadow-[0_0_20px_rgba(239,68,68,0.14)] transition hover:bg-red-500/20 disabled:opacity-60"
              >
                {actionLoading === "block" ? <Loader size={16} className="animate-spin" /> : <Ban size={16} />}
                {isBlocked ? "Unblock" : "Block"}
              </button>
            </div>
          </section>
        </div>
      )}

      <div className="fixed bottom-0 left-0 z-50 w-full">
        <FooterNav />
      </div>
    </div>
  );
}
